import { useContext } from 'react';
import { TableIdContext } from 'contexts/TableIdContext';
import { useAppSelector } from 'hooks';
import { currentTableSlice } from 'reducers/currentTable';

/**
 * Reads from the correct table's state in Redux based on TableIdContext.
 * Drop-in replacement for useAppSelector((state) => state.currentTable.xxx)
 *
 * Usage:
 *   const game = useTableSelector((table) => table.game);
 *   const players = useTableSelector((table) => table.players);
 */
export function useTableSelector<T>(selector: (tableState: any) => T, tableIdOverride?: string): T {
  const contextualGameID = useContext(TableIdContext);
  const gameID = tableIdOverride ?? contextualGameID;
  return useAppSelector((state) => {
    const fromTables = (state as any).tables?.[gameID];
    if (fromTables) return selector(fromTables);
    // Fall back to state.currentTable when:
    //   - the contextual gameID is empty (callers reading currentTable
    //     directly through this hook without a per-table slice, e.g. unit
    //     tests that synthesise a single-table store), OR
    //   - state.currentTable holds THIS table's data (matching gameID).
    // Anything else (currentTable still carries a previously-rendered
    // table while state.tables[gameID] hasn't been hydrated by the first
    // GOT_TEXAS / GOT_OMAHA yet) returns the slice's empty initial state,
    // so the DOM never shows fields from a different table than the URL.
    const currentTable = (state as any).currentTable;
    if (!gameID) return selector(currentTable);
    const currentGid = String(currentTable?.game?.gameID ?? '');
    if (currentGid !== '' && currentGid !== '0' && currentGid === String(gameID)) {
      return selector(currentTable);
    }
    return selector(currentTableSlice.getInitialState());
  });
}

/**
 * Gets the current table's gameID from context.
 */
export function useTableId(): string {
  return useContext(TableIdContext);
}
